import React, { useContext, useEffect, useCallback } from 'react';
import MapContext from '../Map/MapContext';
import { useToolsContext } from '../../context/ToolsContext';
import { useTranslation } from 'react-i18next';
import Tool from '../Tool/Tool';
import DragBox from 'ol/interaction/DragBox';
import { always } from 'ol/events/condition';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import { Style, Stroke, Fill } from 'ol/style';
import Feature from 'ol/Feature';

// Componente que implementa la funcionalidad de "Zoom Box"
const ZoomToBox = () => {
  const { toolStates, setToolState } = useToolsContext();
  const { map } = useContext(MapContext);
  const { t } = useTranslation();

  const zoomBoxState = toolStates?.['zoomBox'] || { active: false };

  const activarZoomBox = useCallback(() => {
    if (!map) return;

    // Crear fuente vectorial y capa para dibujar la caja
    const vectorSource = new VectorSource();
    const vectorLayer = new VectorLayer({
      source: vectorSource,
      style: new Style({
        stroke: new Stroke({
          color: 'rgba(100, 119, 51, 1)', // Color del borde
          width: 3
        }),
        fill: new Fill({
          color: 'rgba(100, 119, 51, 0.4)' // Color del relleno
        })
      }),
      zIndex: 1000 // Asegura que la capa esté por encima de otras capas
    });

    // Asignar un nombre único a la capa para identificarla
    vectorLayer.set('name', 'zoomBoxLayer');

    // Agregar capa al mapa
    map.addLayer(vectorLayer);

    // Crear la interacción DragBox
    const dragBox = new DragBox({
      condition: always, // Habilitar interacción siempre
    });

    // Al iniciar la selección, limpiar el contenido del vector
    dragBox.on('boxstart', () => {
      vectorSource.clear();
    });

    // Mientras se arrastra, dibujar la caja en tiempo real
    dragBox.on('boxdrag', () => {
      vectorSource.clear();
      const geometry = dragBox.getGeometry();
      const feature = new Feature(geometry);
      vectorSource.addFeature(feature);
    });

    // Al finalizar, ajustar la vista al área seleccionada y limpiar
    dragBox.on('boxend', () => {
      const extent = dragBox.getGeometry().getExtent();
      map.getView().fit(extent, { duration: 500 });
      vectorSource.clear(); // Limpiar la selección
      setToolState('zoomBox', { active: false }); // Desactivar herramienta
    });

    // Agregar interacción al mapa
    map.addInteraction(dragBox);

    // Retornar función de limpieza
    return () => {
      map.removeInteraction(dragBox);
      map.removeLayer(vectorLayer);
    };
  }, [map, setToolState]);

  const desactivarZoomBox = useCallback(() => {
    if (!map) return;

    // Eliminar interacciones DragBox activas
    map.getInteractions().getArray().forEach(interaction => {
      if (interaction instanceof DragBox) {
        map.removeInteraction(interaction);
      }
    });

    // Eliminar únicamente la capa vectorial con el nombre "zoomBoxLayer"
    map.getLayers().getArray().forEach(layer => {
      if (layer instanceof VectorLayer && layer.get('name') === 'zoomBoxLayer') {
        map.removeLayer(layer);
      }
    });
  }, [map]);

  // Manejar activación/desactivación de la herramienta
  useEffect(() => {
    if (!map || !toolStates?.zoomBox?.active) return;

    let cleanup;

    if (zoomBoxState.active) {
      cleanup = activarZoomBox();
      document.body.style.cursor = 'crosshair'; // Cambiar cursor a cruz
    } else {
      desactivarZoomBox();
      document.body.style.cursor = ''; // Restaurar cursor predeterminado
    }

    return () => {
      if (cleanup) cleanup();
      desactivarZoomBox();
      document.body.style.cursor = ''; // Restaurar cursor al desmontar
    };
  }, [map, toolStates, zoomBoxState.active, activarZoomBox, desactivarZoomBox]);

  if (!map || !toolStates) return null;

  // Renderizar botón de herramienta
  return (
    <Tool
      id="zoomBox"
      type="clickable"
      iconNode={<svg fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" height="1em" width="1em" xmlns="http://www.w3.org/2000/svg" style={{overflow:'visible'}}><path d="M4 8V6a2 2 0 0 1 2-2h2M4 16v2a2 2 0 0 0 2 2h2M16 4h2a2 2 0 0 1 2 2v2M16 20h2a2 2 0 0 0 2-2v-2M7 12c3.333-4.667 6.667-4.667 10 0M7 12c3.333 4.667 6.667 4.667 10 0M12 12h-.01"/></svg>}
      label={t('Zoom Box')}
      sidebarTitle={t('Zoom Box')}
    />
  );
};

export default ZoomToBox;
